import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import Modal from '../../components/Modal';
import Pagination from '../../components/common/Pagination';
import ManagementHeader from './ManagementHeader';
import { fetchFactoriesForManagement, deleteFactory } from '../../services/api';
import { hasPermission } from '../../services/authService';
import { useTranslation } from '../../context/LanguageContext';
import './management.css';

const DASH = '—';

/* GET /management/factories 응답 → 화면 행. 값이 없으면 null 로 두고 화면에서 — 로 표시한다 */
const toRow = (factory) => ({
    id: factory.id,
    name: factory.factoryName,
    code: factory.factoryCode,
    location: factory.address || null,
    temperature: factory.temperature != null ? `${factory.temperature}°C` : null,
    humidity: factory.humidity != null ? `${factory.humidity}%` : null,
    equipmentStatus: {
        total: factory.totalEquipment || 0,
        running: factory.runningEquipment || 0,
        idle: factory.idleEquipment || 0,
        stopped: factory.stoppedEquipment || 0
    },
    operationRate: factory.operationRate != null ? factory.operationRate : null,
    status: factory.status === 'NORMAL' ? '정상' : factory.status === 'WARNING' ? '경고' : '이상',
    lastInspection: factory.lastInspectionDate || null,
    manager: factory.managerName || null
});

const STATUS_BADGE = { '정상': 'success', '경고': 'warning', '이상': 'error' };

const rateClass = (rate) => {
    if (rate >= 85) return 'high';
    if (rate >= 70) return 'medium';
    return 'low';
};

function FactoryListPage() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [selectedStatus, setSelectedStatus] = useState('전체');
    const [searchQuery, setSearchQuery] = useState('');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedFactory, setSelectedFactory] = useState(null);
    const [factories, setFactories] = useState([]);
    const [allFactories, setAllFactories] = useState([]);
    const [loading, setLoading] = useState(true);

    // 페이지네이션 상태
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);

    // 권한 체크
    const canCreate = hasPermission('factory.create');
    const canUpdate = hasPermission('factory.update');
    const canDelete = hasPermission('factory.delete');

    const loadFactories = useCallback(async () => {
        const data = await fetchFactoriesForManagement();
        const rows = (Array.isArray(data) ? data : []).map(toRow);
        setAllFactories(rows);
        setFactories(rows);
    }, []);

    // API에서 공장 목록 가져오기
    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                await loadFactories();
            } catch (err) {
                console.error('공장 목록 조회 실패:', err);
                setAllFactories([]);
                setFactories([]);
            } finally {
                setLoading(false);
            }
        })();
    }, [loadFactories]);

    // 필터링 로직
    useEffect(() => {
        let filtered = [...allFactories];

        if (selectedStatus !== '전체') {
            filtered = filtered.filter(factory => factory.status === selectedStatus);
        }

        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            filtered = filtered.filter(factory =>
                (factory.name || '').toLowerCase().includes(query) ||
                (factory.code || '').toLowerCase().includes(query)
            );
        }

        setFactories(filtered);
        setPage(0); // 필터 변경 시 첫 페이지로
    }, [selectedStatus, searchQuery, allFactories]);

    // 페이지네이션 적용
    const paginatedFactories = useMemo(() => {
        const startIndex = page * size;
        return factories.slice(startIndex, startIndex + size);
    }, [factories, page, size]);

    const totalElements = factories.length;
    const totalPages = Math.ceil(totalElements / size);

    const handleSizeChange = (newSize) => {
        setSize(newSize);
        setPage(0);
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case '정상':
                return t('pages.management.factoryList.status.normal');
            case '경고':
                return t('pages.management.factoryList.status.warning');
            case '이상':
                return t('pages.management.factoryList.status.error');
            default:
                return status;
        }
    };

    const handleDeleteClick = (factory) => {
        setSelectedFactory(factory);
        setIsDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await deleteFactory(selectedFactory.id);
            alert(t('pages.management.factoryList.alertDeleted', { name: selectedFactory.name }));
            setIsDeleteModalOpen(false);
            await loadFactories(); // 목록 새로고침
        } catch (error) {
            console.error('공장 삭제 실패:', error);
            alert(t('pages.management.factoryList.alertDeleteFail'));
        }
    };

    const colCount = 10;

    return (
        <MainLayout>
            <div className="mg-page">
                <ManagementHeader crumbs={[{ label: t('sidebar.management.factoryList') }]}>
                    {canCreate && (
                        <button type="button" className="mg-btn primary" onClick={() => navigate('/factory/register')}>
                            <span className="material-symbols-outlined">add</span>
                            {t('pages.management.factoryList.addBtn')}
                        </button>
                    )}
                </ManagementHeader>

                <div className="mg-panel fill">
                    <div className="mg-toolbar">
                        <label className="mg-field">
                            {t('pages.management.factoryList.label.status')}
                            <select className="mg-select" value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                                <option value="전체">{t('pages.management.factoryList.status.all')}</option>
                                <option value="정상">{t('pages.management.factoryList.status.normal')}</option>
                                <option value="경고">{t('pages.management.factoryList.status.warning')}</option>
                                <option value="이상">{t('pages.management.factoryList.status.error')}</option>
                            </select>
                        </label>
                        <div className="mg-toolbar-right">
                            <div className="mg-search">
                                <input
                                    type="text"
                                    className="mg-input"
                                    placeholder={t('pages.management.factoryList.searchPlaceholder')}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                                />
                                <span className="material-symbols-outlined">search</span>
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <div className="mg-loading">{t('pages.management.factoryList.loading')}</div>
                    ) : (
                        <>
                            <div className="mg-table-wrap">
                                <table className="mg-table">
                                    <thead>
                                        <tr>
                                            <th className="left">{t('pages.management.factoryList.col.name')}</th>
                                            <th>{t('pages.management.factoryList.col.code')}</th>
                                            <th className="left">{t('pages.management.factoryList.col.location')}</th>
                                            <th>{t('pages.management.factoryList.col.temperature')}</th>
                                            <th>{t('pages.management.factoryList.col.humidity')}</th>
                                            <th>{t('pages.management.factoryList.col.equipmentStatus')}</th>
                                            <th>{t('pages.management.factoryList.col.operationRate')}</th>
                                            <th>{t('pages.management.factoryList.col.status')}</th>
                                            <th>{t('pages.management.factoryList.col.lastInspection')}</th>
                                            <th>{t('pages.management.factoryList.col.action')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedFactories.length === 0 && (
                                            <tr className="mg-empty-row"><td className="mg-empty" colSpan={colCount}>{t('common.noData')}</td></tr>
                                        )}
                                        {paginatedFactories.map((factory) => (
                                            <tr key={factory.id} onClick={() => navigate(`/factory/${factory.id}`)}>
                                                <td className="left">
                                                    <div className="mg-name">
                                                        <span className="mg-name-icon"><span className="material-symbols-outlined">factory</span></span>
                                                        <div>
                                                            <div className="mg-name-main">{factory.name}</div>
                                                            <div className="mg-name-sub">{t('pages.management.factoryList.manager', { manager: factory.manager || DASH })}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td><span className="mg-code">{factory.code}</span></td>
                                                <td className="left">{factory.location || DASH}</td>
                                                <td>
                                                    <span className="mg-env"><span className="material-symbols-outlined">thermostat</span>{factory.temperature || DASH}</span>
                                                </td>
                                                <td>
                                                    <span className="mg-env"><span className="material-symbols-outlined">water_drop</span>{factory.humidity || DASH}</span>
                                                </td>
                                                <td>
                                                    <div className="mg-equip">
                                                        <span className="mg-equip-total">{t('pages.management.factoryList.equipmentTotal', { total: factory.equipmentStatus.total })}</span>
                                                        <span className="mg-equip-split">
                                                            <span className="running">{t('pages.management.factoryList.equipmentRunning', { running: factory.equipmentStatus.running })}</span>
                                                            <span className="idle">{t('pages.management.factoryList.equipmentIdle', { idle: factory.equipmentStatus.idle })}</span>
                                                            <span className="stopped">{t('pages.management.factoryList.equipmentStopped', { stopped: factory.equipmentStatus.stopped })}</span>
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    {factory.operationRate == null ? DASH : (
                                                        <span className="mg-rate">
                                                            <span className="mg-rate-track">
                                                                <span className={`mg-rate-bar ${rateClass(factory.operationRate)}`} style={{ width: `${Math.min(100, factory.operationRate)}%` }} />
                                                            </span>
                                                            <span className="mg-rate-text">{factory.operationRate}%</span>
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    <span className={`mg-badge ${STATUS_BADGE[factory.status] || 'neutral'}`}>{getStatusLabel(factory.status)}</span>
                                                </td>
                                                <td className={factory.lastInspection ? '' : 'mg-muted'}>{factory.lastInspection || DASH}</td>
                                                <td>
                                                    <div className="mg-actions" onClick={(e) => e.stopPropagation()}>
                                                        {canUpdate && (
                                                            <button type="button" className="mg-icon-btn" onClick={() => navigate(`/factory/${factory.id}?edit=true`)}>
                                                                <span className="material-symbols-outlined">edit</span>
                                                            </button>
                                                        )}
                                                        {canDelete && (
                                                            <button type="button" className="mg-icon-btn danger" onClick={() => handleDeleteClick(factory)}>
                                                                <span className="material-symbols-outlined">delete</span>
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <Pagination
                                page={page}
                                totalPages={totalPages}
                                totalElements={totalElements}
                                size={size}
                                onPageChange={setPage}
                                onSizeChange={handleSizeChange}
                            />
                        </>
                    )}
                </div>
            </div>

            <Modal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDeleteConfirm}
                title={t('pages.management.factoryList.deleteModal.title')}
                message={selectedFactory ? t('pages.management.factoryList.deleteModal.message', { name: selectedFactory.name }) : ''}
                confirmText={t('pages.management.factoryList.deleteModal.confirm')}
                cancelText={t('pages.management.factoryList.deleteModal.cancel')}
                type="danger"
            />
        </MainLayout>
    );
}

export default FactoryListPage;
